// Some questions (Data Interpretation → Tabulation) carry a small table INSIDE their
// text, laid out with " | " column separators and a row of dashes:
//
//   The table gives the units sold by four stores over five years.
//
//   Store   | 2019 | 2020
//   -------------------------
//   Store 1 |  650 |  760
//
//   What is the average number for Store 1?
//
// Rendered as ordinary text the line breaks and spacing collapse into one unreadable
// run-on paragraph, so the table has to be recognised and drawn as a real table.
const SEPARATOR_LINE = /^[-=+|\s]{5,}$/;

/** @returns {{ header: string[], rows: string[][] } | null} null when the block is not a table */
export function parseTable(block) {
  const lines = String(block ?? '').split('\n').map(l => l.replace(/\s+$/, '')).filter(l => l.trim() !== '');
  const dataLines = lines.filter(l => !SEPARATOR_LINE.test(l));
  if (dataLines.length < 2 || !dataLines.every(l => l.includes('|'))) return null;
  // A leading/trailing pipe is a border, not an empty column ("| a | b |" → a, b).
  const rows = dataLines.map(l => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim()));
  const cols = rows[0].length;
  if (cols < 2 || rows.some(r => r.length !== cols)) return null;   // ragged → not safe to guess, keep as text
  return { header: rows[0], rows: rows.slice(1) };
}

/** Splits question text on blank lines into `{type:'text'}` and `{type:'table'}` blocks. */
export function splitQuestionText(text) {
  const raw = String(text ?? '');
  if (!raw.includes('\n') && !raw.includes('\r')) return [{ type: 'text', text: raw }];   // ordinary questions: untouched
  const src = raw.replace(/\r\n?/g, '\n');
  return src.split(/\n[ \t]*\n/)
    .map(block => { const t = parseTable(block); return t ? { type: 'table', ...t } : { type: 'text', text: block.trim() }; })
    .filter(b => b.type === 'table' || b.text !== '');
}

export const hasTable = (text) => splitQuestionText(text).some(b => b.type === 'table');
